import { useState, useRef, useEffect } from 'react';
import { useSnapshot } from 'valtio';
import { Play, CheckCircle2, Circle, X } from 'lucide-react';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Dialog, DialogContent } from './ui/dialog';
import { ONBOARDING_VIDEOS } from '@/lib/onboarding-videos';
import { store, actions } from '@/store';

interface OnboardingProps {
  open: boolean;
  onClose: () => void;
  initialVideoId?: string;
}

export function Onboarding({ open, onClose, initialVideoId }: OnboardingProps) {
  const snap = useSnapshot(store);
  const [selectedVideoId, setSelectedVideoId] = useState(initialVideoId || ONBOARDING_VIDEOS[0].id);
  const videoRef = useRef<HTMLVideoElement>(null);

  const selectedVideo = ONBOARDING_VIDEOS.find((v) => v.id === selectedVideoId);
  const completedVideoIds = snap.completedVideoIds;

  // Autoplay when video changes
  useEffect(() => {
    if (open && videoRef.current) {
      videoRef.current.play().catch((error) => {
        console.log('Autoplay prevented:', error);
      });
    }
  }, [selectedVideoId, open]);

  const handleVideoSelect = (videoId: string) => {
    setSelectedVideoId(videoId);
  };

  const handleVideoEnd = () => {
    // Mark current video as completed
    actions.markVideoCompleted(selectedVideoId);

    // Auto-advance to next video
    const currentIndex = ONBOARDING_VIDEOS.findIndex((v) => v.id === selectedVideoId);
    if (currentIndex < ONBOARDING_VIDEOS.length - 1) {
      const nextVideo = ONBOARDING_VIDEOS[currentIndex + 1];
      setSelectedVideoId(nextVideo.id);
    }
  };

  const completedCount = ONBOARDING_VIDEOS.filter((v) =>
    completedVideoIds.includes(v.id)
  ).length;
  const progress = Math.round((completedCount / ONBOARDING_VIDEOS.length) * 100);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl h-[85vh] p-0 gap-0">
        <div className="flex h-full">
          {/* Sidebar with video list */}
          <div className="w-80 border-r bg-muted/30 flex flex-col">
            <div className="p-6 border-b">
              <div className="flex items-start justify-between mb-2">
                <h2 className="text-2xl font-semibold">Getting Started</h2>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 -mt-1"
                  onClick={onClose}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Watch these videos to learn the basics
              </p>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Progress</span>
                  <span className="font-medium">
                    {completedCount}/{ONBOARDING_VIDEOS.length}
                  </span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            </div>

            <ScrollArea className="flex-1">
              <div className="p-4 space-y-2">
                {ONBOARDING_VIDEOS.map((video, index) => {
                  const isCompleted = completedVideoIds.includes(video.id);
                  const isSelected = selectedVideoId === video.id;

                  return (
                    <button
                      key={video.id}
                      onClick={() => handleVideoSelect(video.id)}
                      className={`w-full text-left p-4 rounded-lg transition-colors ${
                        isSelected
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-background hover:bg-muted'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-1">
                          {isCompleted ? (
                            <CheckCircle2 className={`h-5 w-5 ${isSelected ? 'text-primary-foreground' : 'text-green-500'}`} />
                          ) : isSelected ? (
                            <Play className="h-5 w-5" fill="currentColor" />
                          ) : (
                            <Circle className="h-5 w-5 opacity-50" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <span className="text-sm font-medium">
                              {index + 1}. {video.title}
                            </span>
                          </div>
                          <span
                            className={`text-xs ${
                              isSelected
                                ? 'opacity-90'
                                : 'text-muted-foreground'
                            }`}
                          >
                            {video.duration}
                          </span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </ScrollArea>

            <div className="p-4 border-t">
              <Button
                onClick={onClose}
                variant="outline"
                className="w-full"
              >
                Continue later
              </Button>
            </div>
          </div>

          {/* Video player area */}
          <div className="flex-1 flex flex-col bg-black relative">
            <div className="flex-1 flex items-center justify-center">
              {selectedVideo && (
                <video
                  ref={videoRef}
                  key={selectedVideo.id}
                  className="w-full h-full"
                  controls
                  onEnded={handleVideoEnd}
                  autoPlay
                  playsInline
                >
                  <source src={selectedVideo.videoUrl} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              )}
            </div>

            {/* Video info overlay */}
            <div className="absolute top-0 left-0 right-0 p-6 bg-gradient-to-b from-black/60 to-transparent pointer-events-none">
              <div className="text-white">
                <h3 className="text-xl font-semibold mb-1">
                  {selectedVideo?.title}
                </h3>
                <p className="text-sm text-white/80">
                  Video {ONBOARDING_VIDEOS.findIndex((v) => v.id === selectedVideoId) + 1}{' '}
                  of {ONBOARDING_VIDEOS.length}
                </p>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
